import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import { Search, RefreshCw, ChevronLeft, ChevronRight, Eye } from "lucide-react";

interface AuditLog {
  id: string;
  created_at: string;
  user_id: string | null;
  user_email: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  details: Record<string, unknown> | null;
}

const ACTION_COLORS: Record<string, string> = {
  create: "bg-green-500/10 text-green-600 border-green-500/20",
  update: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  delete: "bg-red-500/10 text-red-600 border-red-500/20",
  login: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  logout: "bg-gray-500/10 text-gray-600 border-gray-500/20",
  login_failed: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  session_timeout: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  purge: "bg-pink-500/10 text-pink-600 border-pink-500/20",
};

const RESOURCE_TYPES = [
  "all",
  "user_roles",
  "allowed_domains",
  "orders",
  "drivers",
  "tractors",
  "customers",
  "locations",
  "auth",
];

const ACTIONS = ["all", "create", "update", "delete", "login", "logout", "login_failed", "session_timeout", "purge"];

const PAGE_SIZE = 50;

export function AuditLogsTab() {
  const [resourceFilter, setResourceFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const { data: logs, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["audit-logs", resourceFilter, actionFilter, searchQuery, page],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("read_audit_logs", {
        _resource_filter: resourceFilter === "all" ? null : resourceFilter,
        _action_filter: actionFilter === "all" ? null : actionFilter,
        _search_query: searchQuery || null,
        _page_offset: page * PAGE_SIZE,
        _page_size: PAGE_SIZE,
      });
      if (error) throw error;
      return (data ?? []) as AuditLog[];
    },
  });

  const getDetailsSummary = (details: Record<string, unknown> | null) => {
    if (!details) return "-";
    
    // For updates, show what changed
    if (details.old && details.new) {
      const changes: string[] = [];
      const oldObj = details.old as Record<string, unknown>;
      const newObj = details.new as Record<string, unknown>;
      
      for (const key of Object.keys(newObj)) {
        if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
          changes.push(key);
        }
      }
      return changes.length > 0 ? `Changed: ${changes.join(", ")}` : "No changes";
    }
    
    return "Has details";
  };

  const renderDetailsContent = (log: AuditLog) => {
    if (!log.details) return <p className="text-muted-foreground">No details available.</p>;

    const details = log.details;

    // For updates, show old vs new comparison
    if (details.old && details.new) {
      const oldObj = details.old as Record<string, unknown>;
      const newObj = details.new as Record<string, unknown>;
      const changedKeys = Object.keys(newObj).filter(
        (key) => JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])
      );

      if (changedKeys.length === 0) {
        return <p className="text-muted-foreground">No fields were changed.</p>;
      }

      return (
        <div className="space-y-4">
          <h4 className="font-medium text-sm">Changed Fields:</h4>
          <div className="space-y-3">
            {changedKeys.map((key) => (
              <div key={key} className="border rounded-md p-3 space-y-2">
                <p className="font-medium text-sm">{key}</p>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="text-muted-foreground mb-1">Before:</p>
                    <pre className="bg-muted p-2 rounded overflow-auto max-h-32">
                      {JSON.stringify(oldObj[key], null, 2) || "null"}
                    </pre>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">After:</p>
                    <pre className="bg-muted p-2 rounded overflow-auto max-h-32">
                      {JSON.stringify(newObj[key], null, 2) || "null"}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // For creates/deletes/other, show full details
    return (
      <div className="space-y-2">
        <h4 className="font-medium text-sm">Full Details:</h4>
        <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-96">
          {JSON.stringify(details, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Audit Logs</CardTitle>
          <CardDescription>
            Track all actions performed in the system for compliance and security monitoring.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email or resource ID..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                className="w-64"
              />
            </div>
            <Select
              value={resourceFilter}
              onValueChange={(v) => {
                setResourceFilter(v);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Resource type" />
              </SelectTrigger>
              <SelectContent>
                {RESOURCE_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type === "all" ? "All Resources" : type.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={actionFilter}
              onValueChange={(v) => {
                setActionFilter(v);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                {ACTIONS.map((action) => (
                  <SelectItem key={action} value={action}>
                    {action === "all" ? "All Actions" : action.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {/* Table */}
          <ScrollArea className="h-[500px] rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="w-[120px]">Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Resource ID</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Loading audit logs...
                    </TableCell>
                  </TableRow>
                ) : logs?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No audit logs found.
                    </TableCell>
                  </TableRow>
                ) : (
                  logs?.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {format(new Date(log.created_at), "MMM d, yyyy HH:mm:ss")}
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.user_email || "System"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={ACTION_COLORS[log.action] || ""}
                        >
                          {log.action.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.resource_type.replace("_", " ")}
                      </TableCell>
                      <TableCell className="font-mono text-xs max-w-[150px] truncate">
                        {log.resource_id || "-"}
                      </TableCell>
                      <TableCell>
                        {log.details ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            {getDetailsSummary(log.details)}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {logs?.length || 0} records (page {page + 1})
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={(logs?.length || 0) < PAGE_SIZE}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Audit Log Details
              {selectedLog && (
                <Badge variant="outline" className={ACTION_COLORS[selectedLog.action] || ""}>
                  {selectedLog.action.replace("_", " ")}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Timestamp</p>
                  <p className="font-mono">{format(new Date(selectedLog.created_at), "PPpp")}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">User</p>
                  <p>{selectedLog.user_email || "System"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Resource Type</p>
                  <p>{selectedLog.resource_type.replace("_", " ")}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Resource ID</p>
                  <p className="font-mono text-xs break-all">{selectedLog.resource_id || "-"}</p>
                </div>
              </div>
              <div className="border-t pt-4">
                {renderDetailsContent(selectedLog)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
